import React, { Component, createRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { isMobileUA } from 'dpl/shared/utils';

import FloatingTooltip from './FloatingTooltip';

const PORTAL_MOUNT_EL = window.document.body;

const IS_MOBILE_UA = isMobileUA();

const INITIAL_POSITION = {
  top: -9999,
  left: -9999
};

const TOOLTIP_POSITIONS = {
  BOTTOM_CENTER: 'bottomCenter',
  BOTTOM_LEFT: 'bottomLeft',
  BOTTOM_RIGHT: 'bottomRight'
};

const HORIZONTAL_PADDING = 40;
const ARROW_PADDING = 12;

export default class FloatingTooltipWrapper extends Component {
  static propTypes = {
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
    title: PropTypes.node.isRequired,
    isVisible: PropTypes.bool,
    minWidth: PropTypes.string,
    hideDelay: PropTypes.number,
    omitArrow: PropTypes.bool,
    position: PropTypes.oneOf(Object.values(TOOLTIP_POSITIONS))
  };

  static defaultProps = {
    className: '',
    isVisible: false,
    position: TOOLTIP_POSITIONS.BOTTOM_CENTER,
    minWidth: '100%',
    hideDelay: 300,
    omitArrow: false
  };

  state = {
    isVisible: this.props.isVisible,
    mouseInBody: false,
    portalPosition: INITIAL_POSITION
  };

  safeSetState = this.setState;

  anchorRef = createRef();

  tooltipRef = createRef();

  handleMouseEnter = () => {
    this.setState({
      mouseInBody: true,
      isVisible: true
    });
  };

  handleMouseLeave = () => {
    this.setState({ mouseInBody: false });

    window.setTimeout(() => {
      if (!this.state.mouseInBody) {
        this.safeSetState({ isVisible: false });
      }
    }, this.props.hideDelay);
  };

  handleClick = (e) => {
    e.preventDefault();
    const newValue = !this.state.isVisible;
    this.setState({
      isVisible: newValue,
      mouseInBody: newValue
    });
  };

  componentWillUnmount() {
    this.safeSetState = () => {};
  }

  getLeftOffset = (left, containerWidth) => {
    const { position } = this.props;
    const tooltipWidth = this.tooltipRef.current.clientWidth;
    const maxLeftOffset = tooltipWidth / 2 - ARROW_PADDING;
    const minLeftOffset = -maxLeftOffset;

    if (position === TOOLTIP_POSITIONS.BOTTOM_LEFT) {
      return minLeftOffset;
    }

    if (position === TOOLTIP_POSITIONS.BOTTOM_RIGHT) {
      return maxLeftOffset;
    }

    if (position === TOOLTIP_POSITIONS.BOTTOM_CENTER) {
      const rightBleed = (left + tooltipWidth / 2) - containerWidth;
      const leftBleed = tooltipWidth / 2 - left;

      if (rightBleed > 0) {
        return Math.max(minLeftOffset, -(rightBleed + HORIZONTAL_PADDING));
      }

      if (leftBleed > 0) {
        return Math.min(maxLeftOffset, leftBleed + HORIZONTAL_PADDING);
      }
    }

    return 0;
  };

  updatePositionIfNeeded = (prevState) => {
    const { isVisible } = this.state;

    if (isVisible === prevState.isVisible) {
      return false;
    }

    if (!isVisible) {
      window.setTimeout(() => {
        this.safeSetState({
          portalPosition: INITIAL_POSITION
        });
      }, this.props.hideDelay);

      return true;
    }

    const {
      top: containerTop,
      left: containerLeft,
      width: containerWidth
    } = PORTAL_MOUNT_EL.getBoundingClientRect();

    const {
      top: anchorTop,
      left: anchorLeft,
      width: anchorWidth,
      height: anchorHeight
    } = this.anchorRef.current.getBoundingClientRect();

    const left = anchorLeft - containerLeft;

    this.setState({
      leftOffset: this.getLeftOffset(left + anchorWidth / 2, containerWidth),
      portalPosition: {
        left,
        top: anchorTop - containerTop + anchorHeight,
        width: anchorWidth
      }
    });

    return true;
  };

  componentDidUpdate(prevProps, prevState) {
    this.updatePositionIfNeeded(prevState);
  }

  render() {
    const {
      leftOffset,
      isVisible,
      portalPosition: { left, top, width }
    } = this.state;

    const {
      className,
      title,
      children,
      minWidth,
      omitArrow
    } = this.props;

    return (
      <div className={ classnames('FloatingTooltipWrapper dib', className) }>
        <div
          role="button"
          tabIndex="0"
          onMouseEnter={ IS_MOBILE_UA ? null : this.handleMouseEnter }
          onMouseLeave={ this.handleMouseLeave }
          onFocus={ IS_MOBILE_UA ? null : this.handleMouseEnter }
          onBlur={ this.handleMouseLeave }
          onClick={ this.handleClick }
          onKeyPress={ this.handleClick }
          className="pointer"
          ref={ this.anchorRef }
        >
          { children }
        </div>
        {
          createPortal(
            <div
              className="z-max absolute"
              style={ { left: `${left}px`, top: `${top}px`, width: `${width}px` } }
            >
              <div
                style={ { marginLeft: `${leftOffset}px` } }
                className={ classnames('mt2 min-w-100 tc transform-center-x', {
                  'pointer-events-none': !isVisible
                }) }
              >
                <div
                  className={ classnames('transition', {
                    'FloatingTooltipWrapper__tooltip--visible': isVisible,
                    FloatingTooltipWrapper__tooltip: !isVisible
                  }) }
                >
                  <FloatingTooltip
                    setRef={ this.tooltipRef }
                    arrowOffset={ leftOffset }
                    omitArrow={ omitArrow }
                    title={ title }
                    minWidth={ minWidth }
                    onMouseEnter={ this.handleMouseEnter }
                    onMouseLeave={ this.handleMouseLeave }
                  />
                </div>
              </div>
            </div>,
            PORTAL_MOUNT_EL
          )
        }
      </div>
    );
  }
}
